// hooks/useCreditData.js
import { useState, useEffect } from 'react';
import { supabase } from '../../data/supabase';
import { creditService } from '../../services/creditService';

export const useCreditData = (patientId) => {
  const [creditData, setCreditData] = useState({
    packages: [],
    wallets: [],
    transactions: [],
    loading: true,
    error: null
  });

  const fetchCreditData = async () => {
    if (!patientId) return;
    
    try {
      setCreditData(prev => ({ ...prev, loading: true, error: null }));
      
      // Verificar pacotes expirados
      await creditService.checkExpiredPackages(patientId);
      
      // Buscar pacotes
      const { data: packagesData, error: packagesError } = await supabase
        .from('packages')
        .select(`
          *,
          sessions:sessions(*)
        `)
        .eq('patient_id', patientId)
        .order('created_at', { ascending: false });
      
      if (packagesError) throw packagesError;
      
      // Buscar carteiras
      const { data: walletsData, error: walletsError } = await supabase
        .from('wallet')
        .select('*')
        .eq('patient_id', patientId);
      
      if (walletsError) throw walletsError;
      
      // Buscar transações
      let transactionsData = [];
      if (walletsData && walletsData.length > 0) {
        const { data: txData, error: transactionsError } = await supabase
          .from('credit_transactions')
          .select(`
            *,
            wallet:wallet_id(*),
            appointment:appointment_id(*)
          `)
          .in('wallet_id', walletsData.map(w => w.id))
          .order('created_at', { ascending: false });
        
        if (transactionsError) throw transactionsError;
        transactionsData = txData || [];
      }
      
      setCreditData({
        packages: packagesData || [],
        wallets: walletsData || [],
        transactions: transactionsData || [],
        loading: false,
        error: null
      });
    } catch (error) {
      console.error('Erro ao buscar dados de crédito:', error);
      setCreditData(prev => ({
        ...prev,
        loading: false,
        error: 'Erro ao carregar dados de crédito'
      }));
    }
  };

  useEffect(() => {
    fetchCreditData();
  }, [patientId]);

  // Função para calcular saldo por tipo de sessão
  const calculateBalance = (sessionType, sourceType = null) => {
    const filteredTransactions = creditData.transactions.filter(t => {
      const typeMatch = t.wallet?.type === sessionType || 
                       (t.notes && t.notes.toLowerCase().includes(sessionType.toLowerCase()));
      
      if (sourceType) {
        return typeMatch && t.source_type === sourceType;
      }
      return typeMatch;
    });
    
    const totalCredits = filteredTransactions.reduce((sum, t) => 
      sum + (t.amount > 0 ? t.amount : 0), 0);
      
    const usedCredits = filteredTransactions.reduce((sum, t) => 
      sum + (t.amount < 0 ? -t.amount : 0), 0);
      
    return {
      total: totalCredits,
      used: usedCredits,
      available: totalCredits - usedCredits
    };
  };

  // Função para obter todos os tipos de sessão disponíveis
  const getAvailableSessionTypes = () => {
    const types = new Set();
    
    creditData.transactions.forEach(t => {
      if (t.wallet?.type && !['package', 'individual'].includes(t.wallet.type)) {
        types.add(t.wallet.type);
      }
      
      if (t.notes) {
        const lowerNotes = t.notes.toLowerCase();
        if (lowerNotes.includes('individual')) types.add('individual');
        if (lowerNotes.includes('casal')) types.add('casal');
        if (lowerNotes.includes('família') || lowerNotes.includes('familia')) types.add('familia');
      }
    });
    
    // Se não houver tipos específicos, adicionar os padrões
    if (types.size === 0) {
      types.add('individual');
    }
    
    return Array.from(types);
  };

  return {
    ...creditData,
    refetch: fetchCreditData,
    calculateBalance,
    getAvailableSessionTypes
  };
};